import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { match } from 'ts-pattern';

import { Alert, AlertDescription, AlertTitle } from '@nesso/ui/primitives/alert';
import { Button } from '@nesso/ui/primitives/button';

import { subscriptionMeOptions } from '@/entities/subscription';
import { CancelPlanFeedbackForm, useCancelPlan } from '@/features/subscription/cancel-plan';
import { PickPlan } from '@/features/subscription/pick-plan';
import { UpdatePlanSummary, useUpdatePlan } from '@/features/subscription/update-plan';
import {
    SheetBackButton,
    SheetBody,
    SheetClose,
    SheetCloseButton,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetPanel,
    SheetTitle,
} from '@/shared/ui/panels/sheet-panel';

export function ManagePlanSheet() {
    const { data: subscription } = useQuery(subscriptionMeOptions());
    const { selectedPlanCode, selectPlan, clearSelection, confirm, isConfirming, error } =
        useUpdatePlan();
    const cancelPlan = useCancelPlan();

    const view = cancelPlan.isOpen ? 'cancel' : selectedPlanCode ? 'summary' : 'plans';

    const title = match(view)
        .with('cancel', () => 'Confirm Cancellation')
        .with('summary', () => 'Summary')
        .with('plans', () => 'Manage plan')
        .exhaustive();

    const { body, footer } = match(view)
        .with('cancel', () => ({
            body: (
                <>
                    {cancelPlan.error != null && (
                        <Alert variant="destructive" orientation="horizontal">
                            <AlertTitle className="tracking-normal">
                                Unable to cancel plan.
                            </AlertTitle>
                            <AlertDescription>Try again</AlertDescription>
                        </Alert>
                    )}
                    <CancelPlanFeedbackForm
                        feedback={cancelPlan.feedback}
                        onFeedbackChange={cancelPlan.setFeedback}
                        comment={cancelPlan.comment}
                        onCommentChange={cancelPlan.setComment}
                    />
                </>
            ),
            footer: (
                <Button
                    className="w-full"
                    disabled={!cancelPlan.canConfirm}
                    onClick={cancelPlan.confirm}
                >
                    {cancelPlan.isProcessing && <Loader2 className="animate-spin" />}
                    {cancelPlan.isProcessing ? 'Canceling Plan' : 'Confirm Cancellation'}
                </Button>
            ),
        }))
        .with('summary', () => ({
            body: (
                <>
                    {error != null && (
                        <Alert variant="destructive" orientation="horizontal">
                            <AlertTitle className="tracking-normal">
                                Unable to update plan.
                            </AlertTitle>
                            <AlertDescription>Try again</AlertDescription>
                        </Alert>
                    )}
                    <UpdatePlanSummary planCode={selectedPlanCode as string} />
                </>
            ),
            footer: (
                <Button className="w-full" disabled={isConfirming} onClick={confirm}>
                    {isConfirming && <Loader2 className="animate-spin" />}
                    {isConfirming ? 'Updating Plan' : 'Confirm'}
                </Button>
            ),
        }))
        .with('plans', () => ({
            body: (
                <>
                    <PickPlan
                        currentPlanCode={subscription?.planCode}
                        nextPlanCode={subscription?.nextPlanCode}
                        currentPeriodEnd={
                            subscription?.currentPeriodEnd
                                ? new Date(subscription.currentPeriodEnd)
                                : undefined
                        }
                        onSelectPlan={selectPlan}
                    />
                    <Button variant="ghost" className="self-start" onClick={cancelPlan.open}>
                        Cancel Plan
                    </Button>
                </>
            ),
            footer: null,
        }))
        .exhaustive();

    return (
        <SheetPanel
            className="px-0"
            type="subscription/manage-plan"
            side="right"
            size="small"
            showCloseButton={false}
            onInteractOutside={(e) => e.preventDefault()}
            onPointerDownOutside={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => e.preventDefault()}
        >
            <SheetHeader variant="mini" bordered>
                {view !== 'plans' && (
                    <SheetBackButton
                        onClick={view === 'cancel' ? cancelPlan.close : clearSelection}
                        disabled={cancelPlan.isProcessing}
                        aria-label="Go back"
                    />
                )}

                <SheetTitle>{title}</SheetTitle>

                <SheetDescription className="sr-only">
                    Review available plans and change your subscription.
                </SheetDescription>
                <SheetClose asChild>
                    <SheetCloseButton />
                </SheetClose>
            </SheetHeader>
            <SheetBody className="flex flex-1 flex-col gap-6 overflow-y-auto py-10">
                {body}
            </SheetBody>
            {footer && <SheetFooter orientation="stacked">{footer}</SheetFooter>}
        </SheetPanel>
    );
}
